import Phaser from 'phaser'

import type { Character } from '~/game/Character'
import type { RoomLayout } from '~/game/world'

const STUDY_THOUGHTS = [
  'reviewing lessons…',
  'reading the notebook…',
  'studying feedback…',
  'catching up on training…',
]
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)]

export interface ClassroomDeps {
  /** All on-floor characters by userId (the scene's `chars` map). */
  chars: ReadonlyMap<string, Character>
  /** The "Agent Training" room this org designated as the classroom. */
  room: RoomLayout
  /** True if userId is in a meeting OR otherwise unavailable to wander. */
  isUnavailable: (userId: string) => boolean
  /** Cooperative busy-lock shared with the scene's real animations. */
  isBusy: (userId: string) => boolean
  markBusy: (userId: string, on: boolean) => void
}

/**
 * Classroom study visits: agents in LEARNING mode occasionally wander to the
 * org's "Agent Training" pulse room, linger with a study thought, and head
 * home. Same STRICT decorative contract as the water cooler — never touches
 * the EventBus, never moves anyone busy/unavailable, releases its lock the
 * moment someone heads home, and dies with the scene (scene.time only).
 */
export function startClassroom(
  scene: Phaser.Scene,
  deps: ClassroomDeps,
): () => void {
  const { chars, room, isUnavailable, isBusy, markBusy } = deps
  let stopped = false
  const studying = new Set<string>()
  let timer: Phaser.Time.TimerEvent | undefined

  const sendHome = (userId: string): void => {
    const char = chars.get(userId)
    studying.delete(userId)
    if (char) char.goHome()
    markBusy(userId, false)
  }

  const visit = (): void => {
    if (stopped) return
    // Only LEARNING-mode agents study — their charter tells them to solicit
    // feedback and record lessons, so the classroom is literally their spot.
    const eligible = [...chars.values()].filter(
      (c) =>
        c.isAgent &&
        c.learningMode === 'LEARNING' &&
        !isUnavailable(c.userId) &&
        !isBusy(c.userId) &&
        !studying.has(c.userId),
    )
    if (eligible.length === 0) return
    const char = pick(eligible)

    // A loose standing spot on the classroom floor (below the header/board).
    const tx = Phaser.Math.Between(
      Math.round(room.x + 34),
      Math.round(room.x + room.w - 34),
    )
    const ty = Phaser.Math.Between(
      Math.round(room.y + room.h - 64),
      Math.round(room.y + room.h - 30),
    )

    studying.add(char.userId)
    markBusy(char.userId, true)
    // Failsafe: a real animation can interrupt the walk (its onArrive never
    // fires) — sweep the wanderer home so they don't stay locked forever.
    scene.time.delayedCall(30000, () => {
      if (!stopped && studying.has(char.userId)) sendHome(char.userId)
    })
    char.walkTo(tx, ty, () => {
      if (stopped || isUnavailable(char.userId)) {
        sendHome(char.userId)
        return
      }
      scene.time.delayedCall(500, () => {
        if (stopped || !studying.has(char.userId)) return
        char.showThought(pick(STUDY_THOUGHTS))
      })
      const linger = Phaser.Math.Between(9000, 16000)
      scene.time.delayedCall(linger, () => {
        if (!studying.has(char.userId)) return
        sendHome(char.userId)
      })
    })
  }

  // Rare, jittered cadence — a study visit is an occasional flourish that only
  // happens when the org actually has LEARNING agents.
  timer = scene.time.addEvent({
    delay: 75000,
    startAt: -20000, // first possible visit ~95s in
    loop: true,
    callback: () => {
      if (Math.random() < 0.22) visit()
    },
  })

  return () => {
    if (stopped) return
    stopped = true
    timer?.remove(false)
    timer = undefined
    for (const userId of [...studying]) sendHome(userId)
  }
}
